import React, { useEffect, useCallback } from 'react';
import { X, Flame } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';

// Static spark data, deterministic positions avoid re-render flicker
const SPARKS = [
  { id: 0,  x: 42, color: '#f97316', delay: 0.00, dur: 1.90, size: 5, drift: -18 },
  { id: 1,  x: 50, color: '#fbbf24', delay: 0.15, dur: 1.60, size: 4, drift:  12 },
  { id: 2,  x: 56, color: '#6366f1', delay: 0.30, dur: 2.00, size: 3, drift:  -8 },
  { id: 3,  x: 38, color: '#f97316', delay: 0.45, dur: 1.70, size: 6, drift:  20 },
  { id: 4,  x: 62, color: '#fbbf24', delay: 0.10, dur: 1.50, size: 3, drift: -15 },
  { id: 5,  x: 48, color: '#fb923c', delay: 0.60, dur: 1.80, size: 4, drift:   8 },
  { id: 6,  x: 35, color: '#6366f1', delay: 0.25, dur: 2.10, size: 3, drift: -22 },
  { id: 7,  x: 65, color: '#f97316', delay: 0.50, dur: 1.60, size: 5, drift:  18 },
  { id: 8,  x: 52, color: '#fbbf24', delay: 0.80, dur: 1.90, size: 3, drift:  -5 },
  { id: 9,  x: 44, color: '#fb923c', delay: 0.35, dur: 1.70, size: 4, drift:  14 },
  { id: 10, x: 58, color: '#6366f1', delay: 0.70, dur: 2.00, size: 3, drift: -12 },
  { id: 11, x: 40, color: '#fbbf24', delay: 0.05, dur: 1.50, size: 5, drift:  22 },
  { id: 12, x: 60, color: '#f97316', delay: 0.55, dur: 1.80, size: 3, drift: -20 },
  { id: 13, x: 47, color: '#fb923c', delay: 0.90, dur: 2.10, size: 4, drift:  10 },
  { id: 14, x: 53, color: '#6366f1', delay: 0.20, dur: 1.60, size: 3, drift: -16 },
  { id: 15, x: 36, color: '#fbbf24', delay: 0.65, dur: 1.90, size: 5, drift:  25 },
];

const MODAL_CSS = `
  @keyframes scm-backdrop {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes scm-card {
    0%   { opacity: 0; transform: scale(0.88) translateY(18px); }
    60%  { transform: scale(1.02) translateY(-2px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes scm-flame-pulse {
    0%, 100% {
      filter: drop-shadow(0 0 6px #f97316) drop-shadow(0 0 12px #fbbf2466);
      transform: scale(1);
    }
    50% {
      filter: drop-shadow(0 0 16px #f97316) drop-shadow(0 0 32px #fbbf2488);
      transform: scale(1.10);
    }
  }
  @keyframes scm-number-in {
    0%   { opacity: 0; transform: scale(0.55) translateY(10px); }
    65%  { transform: scale(1.08); }
    85%  { transform: scale(0.97); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes scm-text-in {
    from { opacity: 0; transform: translateY(7px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes scm-spark {
    0%   { opacity: 0.9; transform: translateY(0) translateX(0) scale(1); }
    100% { opacity: 0;   transform: translateY(-110px) translateX(var(--scm-drift)) scale(0.15); }
  }
  @keyframes scm-progress {
    from { transform: scaleX(1); }
    to   { transform: scaleX(0); }
  }

  .scm-backdrop  { animation: scm-backdrop  250ms ease-out both; }
  .scm-card      { animation: scm-card      500ms cubic-bezier(0.34, 1.56, 0.64, 1) both; }
  .scm-flame     { animation: scm-flame-pulse 2.2s ease-in-out infinite; }
  .scm-number    { animation: scm-number-in  560ms cubic-bezier(0.34, 1.56, 0.64, 1) 180ms both; }
  .scm-text-in   { animation: scm-text-in   340ms ease-out both; }
  .scm-progress  {
    transform-origin: left center;
    animation: scm-progress 4s linear 620ms both;
  }

  @media (prefers-reduced-motion: reduce) {
    .scm-backdrop,
    .scm-card,
    .scm-flame,
    .scm-number,
    .scm-text-in,
    .scm-progress {
      animation: none !important;
      opacity: 1 !important;
      transform: none !important;
    }
  }
`;

// 4s animation + 620ms delay = auto-close at 4700ms
const AUTO_DISMISS_MS = 4700;

const getMilestone = (streak, t) => {
  if (streak >= 365) return { headline: t('streak.celebration.milestone.365.headline'), sub: t('streak.celebration.milestone.365.sub') };
  if (streak >= 100) return { headline: t('streak.celebration.milestone.100.headline'), sub: t('streak.celebration.milestone.100.sub') };
  if (streak >= 30)  return { headline: t('streak.celebration.milestone.30.headline'),  sub: t('streak.celebration.milestone.30.sub')  };
  if (streak >= 14)  return { headline: t('streak.celebration.milestone.14.headline'),  sub: t('streak.celebration.milestone.14.sub')  };
  if (streak >= 7)   return { headline: t('streak.celebration.milestone.7.headline'),   sub: t('streak.celebration.milestone.7.sub')   };
  if (streak >= 3)   return { headline: t('streak.celebration.milestone.3.headline'),   sub: t('streak.celebration.milestone.3.sub')   };
  return               { headline: t('streak.celebration.milestone.default.headline'),  sub: t('streak.celebration.milestone.default.sub') };
};

const StreakCelebrationModal = ({ streak, onClose }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const { headline, sub } = getMilestone(streak, t);

  const handleClose = useCallback(() => onClose(), [onClose]);

  const handleCTA = () => {
    onClose();
    navigate('/dashboard');
  };

  // Auto-dismiss
  useEffect(() => {
    const timer = setTimeout(handleClose, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [handleClose]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose]);

  return (
    <>
      <style>{MODAL_CSS}</style>

      {/* Backdrop */}
      <div
        className="scm-backdrop fixed inset-0 z-[9999] flex items-center justify-center p-5 bg-black/60 backdrop-blur-[3px]"
        role="dialog"
        aria-modal="true"
        aria-label={`${streak} ${t('streak.celebration.dayStreak')}`}
        onClick={handleClose}
      >
        {/* Card, stopPropagation prevents backdrop-click from firing */}
        <div
          className="scm-card relative w-full max-w-[340px] bg-white rounded-[28px] shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top accent bar */}
          <div className="h-1.5 w-full bg-gradient-to-r from-orange-400 via-indigo-500 to-orange-400" />

          {/* Rising spark particles */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
            {SPARKS.map((s) => (
              <span
                key={s.id}
                style={{
                  position: 'absolute',
                  left: `${s.x}%`,
                  bottom: '44%',
                  width: s.size,
                  height: s.size,
                  borderRadius: '50%',
                  backgroundColor: s.color,
                  '--scm-drift': `${s.drift}px`,
                  animation: `scm-spark ${s.dur}s ease-out ${s.delay}s 2`,
                }}
              />
            ))}
          </div>

          {/* Main content */}
          <div className="relative px-8 pt-8 pb-6 text-center">
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Flame icon with glow */}
            <div className="scm-flame inline-flex mb-3">
              <Flame className="w-16 h-16 text-orange-500" strokeWidth={1.5} />
            </div>

            {/* Streak number */}
            <div className="scm-number font-space font-black text-[76px] leading-none text-black tracking-tight select-none">
              {streak}
            </div>

            {/* "Day Streak" label */}
            <div
              className="scm-text-in text-xs font-bold uppercase tracking-[0.22em] text-gray-400 mt-1.5 mb-5"
              style={{ animationDelay: '340ms' }}
            >
              {t('streak.celebration.dayStreak')}
            </div>

            {/* Milestone headline */}
            <div
              className="scm-text-in font-space font-bold text-[22px] leading-tight text-black mb-2"
              style={{ animationDelay: '440ms' }}
            >
              {headline}
            </div>

            {/* Sub-copy */}
            <p
              className="scm-text-in text-sm text-gray-500 leading-relaxed mb-7"
              style={{ animationDelay: '520ms' }}
            >
              {sub}
            </p>

            {/* CTA button */}
            <button
              onClick={handleCTA}
              className="scm-text-in w-full h-12 bg-black text-white font-bold text-sm rounded-2xl hover:bg-gray-900 active:scale-[0.98] transition-all duration-150 cursor-pointer"
              style={{ animationDelay: '600ms' }}
            >
              {t('streak.celebration.cta')}
            </button>
          </div>

          {/* Auto-dismiss progress bar */}
          <div className="h-[3px] bg-gray-100">
            <div className="scm-progress h-full bg-indigo-500 w-full" />
          </div>
        </div>
      </div>
    </>
  );
};

export default StreakCelebrationModal;
